import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Save, Trash2, Wand2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { sortPlans, monthlyBase, planMonths, planSavings, fmtKr } from '@/utils/planPricing';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const toDraft = (p) => ({
  id: p.id, name: p.nameSv || p.name || '', price: p.price ?? 0, months: planMonths(p) || 1,
  badge: p.badge || '', tagline: p.tagline || '', isPopular: !!p.isPopular, isActive: p.isActive !== false, order: p.order ?? '',
});

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">{label}{children}</label>
);

const PlanRow = ({ d, base, onChange, onSave, onDelete, busy, t }) => {
  const sv = planSavings({ price: Number(d.price), duration: Number(d.months), durationType: 'months' }, base);
  const set = (k) => (e) => onChange({ ...d, [k]: e?.target ? e.target.value : e });
  return (
    <div className={`rounded-2xl border p-4 sm:p-5 bg-white dark:bg-slate-900 ${d.isPopular ? 'border-emerald-400' : 'border-slate-200 dark:border-slate-800'}`} data-testid={`plan-row-${d.id || 'new'}`}>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Field label={t('الاسم (سويدي)', 'Namn')}><Input value={d.name} onChange={set('name')} data-testid="plan-name-input" /></Field>
        <Field label={t('السعر (kr)', 'Pris (kr)')}><Input type="number" min="0" value={d.price} onChange={set('price')} data-testid="plan-price-input" /></Field>
        <Field label={t('المدة (أشهر)', 'Längd (mån)')}><Input type="number" min="1" value={d.months} onChange={set('months')} data-testid="plan-months-input" /></Field>
        <Field label={t('الشارة', 'Märke')}><Input value={d.badge} placeholder="Mest populär" onChange={set('badge')} data-testid="plan-badge-input" /></Field>
        <Field label={t('الترتيب', 'Ordning')}><Input type="number" value={d.order} onChange={set('order')} data-testid="plan-order-input" /></Field>
        <div className="flex flex-col gap-2 justify-end text-xs text-slate-600">
          <label className="flex items-center gap-2"><Switch checked={d.isPopular} onCheckedChange={set('isPopular')} data-testid="plan-popular-switch" /><Star className="w-3.5 h-3.5" />{t('مميزة', 'Framhävd')}</label>
          <label className="flex items-center gap-2"><Switch checked={d.isActive} onCheckedChange={set('isActive')} data-testid="plan-active-switch" />{t('ظاهرة', 'Synlig')}</label>
        </div>
        <div className="col-span-2 md:col-span-6"><Field label={t('وصف قصير (سويدي)', 'Kort text')}><Input value={d.tagline} onChange={set('tagline')} data-testid="plan-tagline-input" /></Field></div>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="plan-row-preview">
          {sv ? <><span className="line-through text-slate-400">{fmtKr(sv.compare)} kr</span> → <b>{fmtKr(d.price)} kr</b> · {t('توفير', 'Spara')} {fmtKr(sv.save)} kr ({sv.pct}%) · ≈ {fmtKr(sv.perMonth)} kr/mån</>
            : <><b>{fmtKr(d.price || 0)} kr</b> · {t('بدون خصم', 'Ingen rabatt')}</>}
        </p>
        <div className="flex gap-2">
          {d.id && <Button variant="outline" size="sm" onClick={onDelete} disabled={busy} className="text-rose-600" data-testid="plan-delete-btn"><Trash2 className="w-4 h-4" /></Button>}
          <Button size="sm" onClick={onSave} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="plan-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}<span className="ms-1">{t('حفظ', 'Spara')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function PricingPlansManager({ token, i18n }) {
  const ar = i18n.language === 'ar';
  const t = (a, s) => (ar ? a : s);
  const headers = { Authorization: `Bearer ${token}` };
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/admin/subscription-plans`, { headers: { Authorization: `Bearer ${token}` } });
    setDrafts(sortPlans(data).map(toDraft));
  }, [token]);
  useEffect(() => { load().catch(() => toast.error('Error')); }, [load]);

  const base = monthlyBase(drafts.filter((d) => d.isActive).map((d) => ({ price: Number(d.price), duration: Number(d.months), durationType: 'months' })));

  const save = async (d, i) => {
    if (!d.name.trim() || Number(d.price) < 0 || Number(d.months) < 1) { toast.error(t('أكمل الاسم والسعر والمدة', 'Fyll i namn, pris och längd')); return; }
    setBusy(i);
    const body = { name: d.name.trim(), nameSv: d.name.trim(), price: Number(d.price), duration: Number(d.months), durationType: 'months', currency: 'sek',
      badge: d.badge.trim() || null, tagline: d.tagline.trim() || null, isPopular: d.isPopular, isActive: d.isActive, order: d.order === '' ? null : Number(d.order) };
    try {
      if (d.id) await axios.put(`${API_URL}/api/admin/subscription-plans/${d.id}`, body, { headers });
      else await axios.post(`${API_URL}/api/admin/subscription-plans`, body, { headers });
      toast.success(t('تم الحفظ', 'Sparat'));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(null); }
  };

  const remove = async (d, i) => {
    if (!window.confirm(t(`حذف «${d.name}» نهائياً؟ المشتركون الحاليون يحتفظون باشتراكهم.`, `Radera "${d.name}" permanent? Aktiva prenumeranter behåller sin tillgång.`))) return;
    setBusy(i);
    try { await axios.delete(`${API_URL}/api/admin/subscription-plans/${d.id}`, { headers }); toast.success(t('تم الحذف', 'Raderad')); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(null); }
  };

  const applyStandard = async () => {
    const msg = t('سيتم ضبط الباقات: شهر 249 kr، 3 أشهر 499 kr (الأكثر شعبية)، 6 أشهر 699 kr (أفضل قيمة)، وحذف الباقة السنوية. اكتب APPLY للتأكيد:',
      'Planerna sätts till: 1 mån 249 kr, 3 mån 499 kr (Mest populär), 6 mån 699 kr (Bästa värde) och årsplanen raderas. Skriv APPLY för att bekräfta:');
    if (window.prompt(msg) !== 'APPLY') return;
    setBusy('std');
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/subscription-plans/apply-standard`, { confirm: 'APPLY' }, { headers });
      toast.success(t(`تم: ${data.updated.length} معدّلة، ${data.created.length} جديدة، ${data.deleted.length} محذوفة`, `Klart: ${data.updated.length} uppdaterade, ${data.created.length} nya, ${data.deleted.length} raderade`));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" dir={ar ? 'rtl' : 'ltr'} data-testid="pricing-plans-manager">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t('الباقات والأسعار', 'Prisplaner')}</h2>
          <p className="text-sm text-slate-500 mt-1">{t('التوفير يُحسب تلقائياً مقارنة بالباقة الشهرية. الدفع لمرة واحدة.', 'Besparingen räknas automatiskt mot månadsplanen. Engångsbetalning.')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setDrafts([...drafts, toDraft({ isActive: true })])} data-testid="plan-add-btn"><Plus className="w-4 h-4 me-1" />{t('باقة جديدة', 'Ny plan')}</Button>
          <Button onClick={applyStandard} disabled={busy === 'std'} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="plan-apply-standard-btn">
            {busy === 'std' ? <Loader2 className="w-4 h-4 animate-spin me-1" /> : <Wand2 className="w-4 h-4 me-1" />}{t('تطبيق الباقات الثلاث', 'Använd 1/3/6 mån')}
          </Button>
        </div>
      </div>
      {drafts.map((d, i) => (
        <PlanRow key={d.id || `new-${i}`} d={d} base={base} t={t} busy={busy === i}
          onChange={(nd) => setDrafts(drafts.map((x, j) => (j === i ? nd : x)))}
          onSave={() => save(d, i)} onDelete={() => remove(d, i)} />
      ))}
    </div>
  );
}
